import { RefreshCw } from "lucide-react";

/**
 * PullToRefreshIndicator — visual chrome for usePullToRefresh.
 *
 * Renders a small floating pill that drops down from the top of the
 * viewport as the user pulls. Hidden when phase === "idle". The parent
 * page is responsible for ensuring it has touch-action: pan-y so the
 * gesture works on iOS / Android Chrome.
 *
 * Props:
 *  - pullDistance: px (0..maxPull)
 *  - phase: "idle" | "pulling" | "ready" | "refreshing"
 *  - threshold: px the gesture must travel to trigger refresh
 */
const PullToRefreshIndicator = ({ pullDistance = 0, phase = "idle", threshold = 70 }) => {
  if (phase === "idle") return null;

  const isReady = phase === "ready";
  const isRefreshing = phase === "refreshing";
  const label = isRefreshing ? "Refreshing…" : isReady ? "Release to refresh" : "Pull to refresh";
  // 0..1 → progress toward threshold
  const progress = Math.min(pullDistance / threshold, 1);
  // Translate the pill from -56 px to (pullDistance - 56) px
  const translateY = Math.max(0, pullDistance - 8);
  // Spin rotation tracks pull progress when not yet refreshing; otherwise CSS spin
  const rotation = isRefreshing ? 0 : progress * 270;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center"
      data-testid="pull-to-refresh-indicator"
      style={{ transform: `translateY(${translateY}px)`, transition: isRefreshing ? "transform 200ms ease" : "none" }}
      aria-hidden={!isRefreshing}
    >
      <div
        className={`mt-2 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium shadow-lg backdrop-blur-md border ${
          isReady || isRefreshing
            ? "bg-primary/90 text-primary-foreground border-primary/30"
            : "bg-background/80 text-foreground border-white/10"
        }`}
      >
        <RefreshCw
          className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`}
          style={isRefreshing ? undefined : { transform: `rotate(${rotation}deg)`, transition: "transform 50ms linear" }}
        />
        <span>{label}</span>
      </div>
    </div>
  );
};

export default PullToRefreshIndicator;
